import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatabaseZap, Plus, RefreshCw, AlertTriangle, ChevronRight, Users, FileSpreadsheet } from 'lucide-react';
import type { ToolGrant } from '@platform/shared-types';
import { InventoryPage } from '../inventory/InventoryChrome';
import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import { TenantPicker } from '../../components/tools/TenantPicker';
import { toastService } from '../../services/toastService';
import { toolsApi, type ToolTenantHit } from '../../services/toolsApi';
import { migrationApi, type MigrationJob } from '../../services/migrationApi';
import {
  RUBY, SOURCES, STATUS_META, StatusPill, Btn, Switch, Chips, Modal, Card, Banner,
  fmtDateTime, sourceLabel, toastApiError, type StatusGroup,
} from './migration/shared';

/**
 * Super Admin → Tools → Data Migration.
 *
 * Every migration job across tenants (newest-updated first), a "New migration"
 * modal, and the per-tenant switch for the CLIENT upload page (the
 * `data-migration` tool grant in tools-service).
 */

const TOOL_KEY = 'data-migration';

type Filter = 'action' | 'progress' | 'done' | 'all';
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'action', label: 'Needs action' },
  { id: 'progress', label: 'In progress' },
  { id: 'done', label: 'Done' },
  { id: 'all', label: 'All' },
];

const groupOf = (j: MigrationJob): StatusGroup => STATUS_META[j.status]?.group || 'progress';

const NewMigrationModal: React.FC<{ onClose: () => void; onCreated: (job: MigrationJob) => void }> = ({ onClose, onCreated }) => {
  const [tenant, setTenant] = useState<ToolTenantHit | null>(null);
  const [source, setSource] = useState('other');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!tenant) return;
    setBusy(true);
    try {
      const { job } = await migrationApi.createJob({ tenantId: tenant.tenantId, source, notes: notes.trim() || undefined });
      toastService.success(`Migration started for ${tenant.name || tenant.tenantId}`);
      onCreated(job);
    } catch (e) {
      toastApiError(e, 'Could not start the migration');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New migration"
      icon={DatabaseZap}
      onClose={onClose}
      busy={busy}
      wide
      footer={(
        <>
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn kind="black" onClick={create} busy={busy} disabled={!tenant}>Start migration</Btn>
        </>
      )}
    >
      <div className="space-y-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Tenant</div>
          {tenant ? (
            <div className="flex items-center gap-3 rounded-lg border border-stone-200 px-3 min-h-[44px]">
              <span className="flex-1 font-medium text-stone-900 truncate">{tenant.name || tenant.tenantId}</span>
              <button type="button" onClick={() => setTenant(null)} className="h-11 px-2 text-sm font-medium" style={{ color: RUBY }}>Change</button>
            </div>
          ) : (
            <TenantPicker bare title="Who is this migration for?" onPick={async (hit) => setTenant(hit)} onClose={onClose} />
          )}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Coming from</div>
          <Chips options={SOURCES} value={source} onChange={setSource} />
        </div>
        <div>
          <label htmlFor="mig-notes" className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Notes (optional)</label>
          <textarea
            id="mig-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Anything the team should know — locations, special pricing, what to leave out…"
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-800 outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/15"
          />
        </div>
        <p className="text-xs text-stone-500">Files can be uploaded on the next screen, or by the client from their Tools page once their upload page is on.</p>
      </div>
    </Modal>
  );
};

const ClientAccessPanel: React.FC = () => {
  const [grants, setGrants] = useState<ToolGrant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picker, setPicker] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await toolsApi.toolTenants(TOOL_KEY);
      setGrants(r.grants || []);
    } catch (e: any) {
      setGrants([]);
      setError(e?.message || 'Could not load client access');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setGrant = async (tenantId: string, name: string | undefined, enabled: boolean) => {
    setBusyId(tenantId);
    try {
      const { grant } = await toolsApi.setGrant(TOOL_KEY, tenantId, { enabled });
      const g: ToolGrant = { ...grant, tenantName: grant.tenantName || name };
      setGrants((prev) => [...(prev || []).filter((x) => x.tenantId !== tenantId), g]);
      toastService.success(`Client upload page ${enabled ? 'on' : 'off'} for ${g.tenantName || tenantId}`);
      if (enabled) setPicker(false);
    } catch (e: any) {
      toastService.error(e?.message || 'Could not change client access');
    } finally {
      setBusyId(null);
    }
  };

  const enabledIds = new Set((grants || []).filter((g) => g.enabled).map((g) => g.tenantId));
  const list = (grants || []).slice().sort((a, b) => Number(b.enabled) - Number(a.enabled) || (a.tenantName || '').localeCompare(b.tenantName || ''));

  return (
    <Card
      title={<span className="flex items-center gap-2"><Users size={16} className="text-stone-500" />Client upload page</span>}
      actions={<Btn kind="outline" icon={<Plus size={16} />} onClick={() => setPicker((v) => !v)}>Turn on for a tenant</Btn>}
    >
      <p className="text-sm text-stone-500 -mt-1 mb-3">
        Tenants with this on see <b className="text-stone-700">Tools → Data Migration</b> in their back office, where they can upload export files and answer questions.
      </p>
      {error && <Banner tone="rose" icon={<AlertTriangle size={16} />}>{error}</Banner>}
      {grants === null ? (
        <RubyLoader variant="section" label="Loading client access" />
      ) : list.length === 0 ? (
        <div className="text-sm text-stone-400 py-2">No tenants have the upload page yet.</div>
      ) : (
        <div className="divide-y divide-stone-100 -mx-5">
          {list.map((g) => (
            <div key={g.tenantId} className="px-5 flex items-center gap-3 min-h-[56px]">
              <div className="flex-1 min-w-0">
                <div className={`font-medium truncate ${g.enabled ? 'text-stone-900' : 'text-stone-400'}`}>{g.tenantName || g.tenantId}</div>
                <div className="text-xs text-stone-400">{g.enabled ? 'On' : 'Off'}</div>
              </div>
              <Switch
                on={g.enabled}
                busy={busyId === g.tenantId}
                label={`Client upload page for ${g.tenantName || g.tenantId}`}
                onClick={() => setGrant(g.tenantId, g.tenantName, !g.enabled)}
              />
            </div>
          ))}
        </div>
      )}
      {picker && (
        <div className="mt-3 -mx-5 -mb-5">
          <TenantPicker
            title="Turn the client upload page on for"
            enabledIds={enabledIds}
            enabledLabel="ON"
            onPick={(hit) => setGrant(hit.tenantId, hit.name, true)}
            onClose={() => setPicker(false)}
          />
        </div>
      )}
    </Card>
  );
};

const DataMigrationListPage: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<MigrationJob[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('action');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await migrationApi.listJobs();
      setJobs(r.jobs || []);
    } catch (e: any) {
      setError(e?.message || 'Could not load migrations');
      setJobs((prev) => prev || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { action: 0, progress: 0, done: 0, all: 0 };
    for (const j of jobs || []) { c[groupOf(j)] += 1; c.all += 1; }
    return c;
  }, [jobs]);
  const shown = (jobs || []).filter((j) => filter === 'all' || groupOf(j) === filter);
  const open = (j: MigrationJob) => navigate(`/tools/data-migration/${encodeURIComponent(j.tenantId)}/${encodeURIComponent(j.jobId)}`);

  const actions = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={load}
        disabled={loading}
        className="h-11 w-11 grid place-items-center rounded-lg border border-neutral-700 text-neutral-300 hover:text-white disabled:opacity-60"
        aria-label="Refresh"
        title="Refresh"
      >
        {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw size={16} />}
      </button>
      <Btn kind="ruby" icon={<Plus size={16} />} onClick={() => setCreating(true)}>New migration</Btn>
    </div>
  );

  const tabs = (
    <div className="flex items-center gap-2 overflow-x-auto text-sm">
      {FILTERS.map((t) => {
        const on = filter === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setFilter(t.id)}
            className={`h-11 px-4 rounded-full whitespace-nowrap font-medium transition ${on ? 'text-white' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}
            style={on ? { background: RUBY } : undefined}
          >
            {t.label}<span className={`ml-1.5 text-xs ${on ? 'text-white/80' : 'text-neutral-500'}`}>{counts[t.id]}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <InventoryPage
      icon={DatabaseZap}
      title="Data Migration"
      crumb="Tools"
      subtitle="Bring a client's catalogue across from another POS"
      actions={actions}
      tabs={tabs}
    >
      <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">
        <div className="space-y-3 min-w-0">
          {error && (
            <Banner tone="rose" icon={<AlertTriangle size={16} />}>
              <div className="flex items-center gap-3"><span className="flex-1">{error}</span><Btn onClick={load}>Retry</Btn></div>
            </Banner>
          )}
          {jobs === null ? (
            <RubyLoader variant="page" label="Loading migrations" />
          ) : shown.length === 0 ? (
            <div className="bg-white rounded-xl border border-stone-200 p-10 text-center">
              <FileSpreadsheet size={28} className="mx-auto text-stone-300" />
              <div className="mt-2 font-medium text-stone-700">{filter === 'action' ? 'Nothing needs you right now' : 'No migrations here'}</div>
              <div className="text-sm text-stone-400 mt-1">Start one with “New migration”, or wait for a client upload.</div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              <div className="hidden md:grid grid-cols-[minmax(0,2fr)_120px_170px_70px_90px_130px_24px] gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-stone-500 bg-stone-50 border-b border-stone-100">
                <span>Tenant</span><span>Source</span><span>Status</span><span className="text-right">Files</span><span className="text-right">To create</span><span>Updated</span><span />
              </div>
              <div className="divide-y divide-stone-100">
                {shown.map((j) => (
                  <button
                    key={`${j.tenantId}/${j.jobId}`}
                    type="button"
                    onClick={() => open(j)}
                    className="w-full text-left px-4 py-3 min-h-[56px] hover:bg-stone-50 grid grid-cols-[minmax(0,1fr)_24px] md:grid-cols-[minmax(0,2fr)_120px_170px_70px_90px_130px_24px] gap-x-3 gap-y-1 items-center"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium text-stone-900 truncate">{j.tenantName || j.tenantId}</span>
                      <span className="block text-xs text-stone-400 truncate md:hidden">
                        {sourceLabel(j.source || j.sourceGuess)} · {(j.files || []).length} files · {fmtDateTime(j.updatedAt)}
                      </span>
                      {j.error && <span className="block text-xs text-rose-700 truncate">{j.error}</span>}
                    </span>
                    <span className="hidden md:block text-sm text-stone-600 truncate">{sourceLabel(j.source || j.sourceGuess)}</span>
                    <span className="order-3 md:order-none col-span-2 md:col-span-1"><StatusPill status={j.status} needsInput={j.needsInput} /></span>
                    <span className="hidden md:block text-sm text-stone-600 text-right">{(j.files || []).length}</span>
                    <span className="hidden md:block text-sm text-stone-900 font-medium text-right">{j.stats?.toCreate ?? '—'}</span>
                    <span className="hidden md:block text-sm text-stone-500">{fmtDateTime(j.updatedAt)}</span>
                    <ChevronRight size={16} className="text-stone-400 row-start-1 col-start-2 md:row-auto md:col-auto" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <ClientAccessPanel />
      </div>

      {creating && (
        <NewMigrationModal
          onClose={() => setCreating(false)}
          onCreated={(job) => { setCreating(false); open(job); }}
        />
      )}
    </InventoryPage>
  );
};

export default DataMigrationListPage;
